import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { ListFeishuEventsResponse } from '@/api/types';
import { useToast } from '@/components/Toast';

export const FAILED_FEISHU_EVENT_WATERMARK_KEY = 'cbwee.failedFeishuEventAlerts.watermark';
export const FAILED_FEISHU_EVENT_ALERTED_IDS_KEY = 'cbwee.failedFeishuEventAlerts.alertedEventIds';
const ALERT_PAGE_SIZE = 200;
const MAX_STORED_ALERTED_IDS = 1000;

function maxRfc3339(values: Array<string | null | undefined>): string {
  return values.filter(Boolean).reduce((max, value) => {
    if (!max) return value as string;
    return Date.parse(value as string) > Date.parse(max) ? (value as string) : max;
  }, '') || new Date(0).toISOString();
}

function readAlertedIds(): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(FAILED_FEISHU_EVENT_ALERTED_IDS_KEY) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : []);
  } catch {
    return new Set();
  }
}

function storeAlertedIds(ids: Set<string>) {
  const values = Array.from(ids).slice(-MAX_STORED_ALERTED_IDS);
  localStorage.setItem(FAILED_FEISHU_EVENT_ALERTED_IDS_KEY, JSON.stringify(values));
}

async function fetchFailedFeishuEventAlerts(watermark: string | null): Promise<ListFeishuEventsResponse> {
  const limit = watermark ? ALERT_PAGE_SIZE : 1;
  const events: ListFeishuEventsResponse['events'] = [];
  let observedAt = '';
  let totalCount = 0;
  let offset = 0;

  do {
    const page = await apiFetch<ListFeishuEventsResponse>(`/api/v1/admin/feishu/events?status=failed&since=${encodeURIComponent(watermark ?? '')}&limit=${limit}&offset=${offset}`);
    if (!observedAt) observedAt = page.observed_at;
    totalCount = page.total_count;
    events.push(...page.events);
    if (!watermark || page.events.length === 0) break;
    offset += page.events.length;
  } while (offset < totalCount);

  return { observed_at: observedAt, total_count: totalCount, events };
}

export function useFailedFeishuEventAlerts() {
  const toast = useToast();
  const [watermark, setWatermark] = useState<string | null>(() =>
    localStorage.getItem(FAILED_FEISHU_EVENT_WATERMARK_KEY),
  );
  const initializedRef = useRef(Boolean(watermark));
  const suppressNextBaselineRef = useRef(false);
  const alertedEventIdsRef = useRef<Set<string>>(readAlertedIds());
  const query = useQuery<ListFeishuEventsResponse>({
    queryKey: ['failed-feishu-event-alerts', watermark],
    queryFn: () => fetchFailedFeishuEventAlerts(watermark),
    refetchInterval: 10_000,
  });

  useEffect(() => {
    if (!query.data) return;
    if (!initializedRef.current) {
      const baseline = maxRfc3339([query.data.observed_at, ...query.data.events.map((event) => event.received_at)]);
      query.data.events.forEach((event) => alertedEventIdsRef.current.add(event.event_id));
      storeAlertedIds(alertedEventIdsRef.current);
      initializedRef.current = true;
      suppressNextBaselineRef.current = true;
      localStorage.setItem(FAILED_FEISHU_EVENT_WATERMARK_KEY, baseline);
      setWatermark(baseline);
      return;
    }
    const previous = watermark;
    if (!previous) return;
    if (suppressNextBaselineRef.current) {
      suppressNextBaselineRef.current = false;
      return;
    }
    const previousTime = Date.parse(previous);
    const newEvents = query.data.events.filter((event) => {
      const eventTime = Date.parse(event.received_at);
      return eventTime > previousTime || (eventTime === previousTime && !alertedEventIdsRef.current.has(event.event_id));
    });
    const next = maxRfc3339([previous, ...query.data.events.map((event) => event.received_at)]);
    if (next !== previous) {
      localStorage.setItem(FAILED_FEISHU_EVENT_WATERMARK_KEY, next);
      setWatermark(next);
    }
    const alertableEvents = newEvents.filter((event) => !alertedEventIdsRef.current.has(event.event_id));
    if (alertableEvents.length > 0) {
      alertableEvents.forEach((event) => alertedEventIdsRef.current.add(event.event_id));
      storeAlertedIds(alertedEventIdsRef.current);
      toast.push({
        variant: 'error',
        durationMs: 0,
        message: <Link to="/ops/feishu-events?status=failed">新的失败 Feishu 事件：{alertableEvents.length} 条，打开失败事件列表</Link>,
      });
    }
  }, [query.data, toast, watermark]);
}
